import { useEffect, useState } from 'react';
import Tooltip from './Tooltip';

/**
 * Generic get-or-default + upsert form for 1:1-per-store (or global) settings
 * resources. `load`/`save` are queries.js functions already bound to any
 * required id by the caller.
 *
 * Pass either a flat `fields` array ([{ key, label, required }]), or
 * `sections` ([{ title, fields }]) to render the fields under labeled
 * section headings.
 *
 * `canEdit` (default true): when false, all fields/uploads are disabled and
 * the Save button is disabled with a tooltip explaining why -- for pages
 * whose backend mutation is restricted to a narrower role than the page's
 * own view access (see useCan).
 *
 * `hideSavedMessage` (default false): suppresses the inline "Saved." text --
 * for callers that show their own save feedback instead (e.g. a toast).
 */
export default function SettingsForm({
	load,
	save,
	fields,
	sections,
	emptyMessage,
	onSaved,
	canEdit = true,
	hideSavedMessage = false,
}) {
	const allFields = sections ? sections.flatMap((s) => s.fields) : fields;
	const [values, setValues] = useState({});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [savedMsg, setSavedMsg] = useState('');
	const [uploadingKey, setUploadingKey] = useState(null);
	// Field value holds the path returned by the upload (stable across the
	// API running on a different port -- see uploads.controller.ts); this
	// tracks the resolved absolute URL separately, just for the <img>
	// preview right after a fresh upload. `load()` already returns fields
	// pre-resolved to absolute URLs, so this only needs filling in here.
	const [previewUrls, setPreviewUrls] = useState({});

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		load().then((result) => {
			if (cancelled) return;
			if (result?.success) setValues(result.data || {});
			setLoading(false);
		});
		return () => {
			cancelled = true;
		};
	}, [load]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		setError('');
		setSavedMsg('');
		const payload = {};
		allFields
			.filter((f) => !f.disabled)
			.forEach((f) => {
				payload[f.key] = values[f.key] ?? '';
			});
		const result = await save(payload);
		setSaving(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to save.');
			return;
		}
		setValues(result.data || payload);
		setSavedMsg('Saved.');
		onSaved?.(result.data);
	};

	if (loading) {
		return <div className='text-sm text-gray-400 py-4'>Loading...</div>;
	}

	const handleFileChange = async (f, e) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file || !f.upload) return;
		setUploadingKey(f.key);
		setError('');
		const result = await f.upload(file);
		setUploadingKey(null);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to upload image.');
			return;
		}
		setPreviewUrls((p) => ({ ...p, [f.key]: result.data.url }));
		setValues((v) => ({ ...v, [f.key]: result.data.path }));
	};

	const renderField = (f) => (
		<div key={f.key}>
			<label>{f.label}</label>
			{f.type === 'file' ? (
				<div>
					{(previewUrls[f.key] || values[f.key]) && (
						<img src={previewUrls[f.key] || values[f.key]} alt='' className='h-12 w-auto object-cover rounded mb-2' />
					)}
					<input
						type='file'
						accept={f.accept}
						className='form-input pt-1.5'
						disabled={uploadingKey === f.key || !canEdit}
						onChange={(e) => handleFileChange(f, e)}
					/>
					{uploadingKey === f.key && <p className='text-xs text-gray-400 mt-1'>Uploading...</p>}
				</div>
			) : (
				<input
					className='form-input'
					required={f.required}
					disabled={f.disabled || !canEdit}
					value={values[f.key] ?? ''}
					onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
				/>
			)}
		</div>
	);

	return (
		<form onSubmit={handleSubmit} className='space-y-6 max-w-lg'>
			{emptyMessage && !values[allFields[0]?.key] && (
				<p className='text-sm text-gray-400'>{emptyMessage}</p>
			)}
			{error && <div className='text-sm text-pi-red'>{error}</div>}
			{savedMsg && !hideSavedMessage && <div className='text-sm text-green-700'>{savedMsg}</div>}
			{sections
				? sections.map((section, index) => (
						<div key={section.title}>
							{index > 0 && <hr className='border-gray-200 mb-6' />}
							<div className='space-y-4'>
								<h2 className='text-sm font-medium text-gray-900'>{section.title}</h2>
								{section.fields.map(renderField)}
							</div>
						</div>
					))
				: fields.map(renderField)}
			<div className='flex items-center gap-2'>
				<button type='submit' disabled={saving || !canEdit} className='btn-primary'>
					{saving ? 'Saving...' : 'Save'}
				</button>
				{!canEdit && <Tooltip content='Only a Platform Administrator can change this setting.' />}
			</div>
		</form>
	);
}
